import React, { ChangeEvent, useEffect, useState } from 'react'
import FormsLogo from 'src/components/FormsLogo'
import { Button, FloatingLabel, Form } from 'react-bootstrap'
import AlertMessage from 'src/components/AlertMessage'
import { EMAIL_VALIDATOR } from 'src/config/validators'
import { invitationValidationCheck, resendActivationEmail, signupUserWithCreds } from 'src/requests/auth'
import Link from 'next/link'
import Loader from 'src/components/Loader'
import { IAlert } from 'src/interfaces/props'
import { useFormBackground } from 'src/helpers/hooks/auth'
import { withOutAuth } from 'src/helpers/hoc'
import Metadata from 'src/components/Metadata'
import { useRouter } from 'next/router'
import { IUserCreation } from 'src/interfaces/auth'

function Signup() {
  const {
    query: { invitation_key, is_topp },
  } = useRouter()
  useFormBackground()
  const [info, setInfo] = useState({
    email: '',
    password: '',
    name: '',
    companyName: '',
    disabled: false,
  })

  const [alert, setAlert] = useState<IAlert>()
  const [steps, setSteps] = useState(0)
  const [working, setWorking] = useState(false)

  const checkValidation = async () => {
    try {
      const { data } = await invitationValidationCheck(invitation_key as string)
      setInfo({
        ...info,
        disabled: true,
        email: data.data.email,
        companyName: data.data.organization,
      })
    } catch (err: any) {
      setAlert({ text: err?.response?.data?.message || err.message, type: 'error' })
    }
  }

  useEffect(() => {
    if (!invitation_key) return
    checkValidation()
  }, [invitation_key])
  const handleNext = (nextStep: number) => {
    const { email, password, name } = info
    if (steps <= 2) {
      if (steps === 0) {
        if (email.length > 0 && EMAIL_VALIDATOR.test(email) && password.length > 0) {
          setSteps(nextStep)
        } else {
          setAlert({ text: 'Email or Password is not valid', type: 'error' })
        }
      } else if (steps === 1) {
        if (name.length > 5) {
          setSteps(nextStep)
        } else {
          setAlert({ text: 'Name is not correctly formatted', type: 'error' })
        }
      }
    } else setSteps(nextStep)
  }

  const handleInput = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target
    setInfo({ ...info, [name]: value })
  }
  const handleSubmit = async () => {
    const { email, password, name, companyName: organization_name } = info
    const creds: IUserCreation = {
      email,
      password,
      name,
      organization_name,
    }

    //if invite key hide topp radio buttons

    if (invitation_key) {
      creds.invitation_key = invitation_key
      creds.is_topp = is_topp ? true : false
    }
    try {
      await signupUserWithCreds(creds)
      setWorking(true)

      setSteps(2)
    } catch (error: any) {
      setAlert({
        text: error?.response?.data?.message || error.message || 'Something went wrong',
        type: 'error',
      })
    } finally {
      setWorking(false)
    }
  }

  const handleResend = async () => {
    const { email } = info
    try {
      setWorking(true)
      const { data } = await resendActivationEmail(email)
      setAlert({ text: data?.message || 'Mail sent', type: 'success' })
    } catch (error: any) {
      setAlert({
        text: error?.response?.data?.message || 'Something went wrong',
        type: 'error',
      })
    } finally {
      setWorking(false)
    }
  }

  return (
    <div className='CreateAccountForm'>
      <Metadata title='Quick Organic Signup' />

      {working && <Loader />}

      {alert && <AlertMessage {...alert} />}

      <div>
        <Form className='form_login color_bg-white color_border-gray--08'>
          <div className='d-flex justify-content-center'>
            <FormsLogo />
          </div>
          {steps === 0 ? (
            <div>
              <FloatingLabel controlId='floatingInput' label='Email Address' className='mb-3'>
                <Form.Control type='email' placeholder='Email Address' name='email' className={'form_input'} value={info.email} onChange={handleInput} disabled={info.disabled} />
              </FloatingLabel>

              <FloatingLabel controlId='floatingPassword' label='Password'>
                <Form.Control type='password' placeholder='Password' name='password' className={'form_input'} value={info.password} onChange={handleInput} />
              </FloatingLabel>
              <br />
              <Button variant='primary' type='button' className='form_btn fw-400 w-100' name='step1' onClick={() => handleNext(1)}>
                Create Account
              </Button>

              <p className='fw-400 color_text-gray--01 text-center' style={{ marginTop: '1em' }}>
                <span>Already have an account?</span>
                &nbsp;
                <Link href='/login'>
                  <a className='form_btn-no-outline color_text-primary'>Login</a>
                </Link>
              </p>
            </div>
          ) : steps === 1 ? (
            <div className='signup_topp-container'>
              <p className='fw-400 color_text-gray--01 text-center' style={{ marginTop: '0em', marginBottom: '1em' }}>
                Tell us about your operation
              </p>
              <FloatingLabel controlId='floatingInput' label='Your Name' className='mb-3'>
                <Form.Control type='text' placeholder='Your Name' name='name' value={info.name} className='form_input' onChange={handleInput} />
              </FloatingLabel>

              <FloatingLabel controlId='floatingPassword' label='Company Name' className='mb-3'>
                <Form.Control type='text' placeholder='Company Name' name='companyName' value={info.companyName} className='form_input' onChange={handleInput} disabled={info.disabled} />
              </FloatingLabel>

              <Button variant='primary' type='button' className='form_btn w-100' name='step2' onClick={handleSubmit} disabled={!info.name || !info.companyName ? true : false}>
                {' '}
                Create Account{' '}
              </Button>
            </div>
          ) : steps === 2 ? (
            <div>
              <p className='fw-400 color_text-gray--01 text-center' style={{ marginTop: '0em', marginBottom: '1em' }}>
                Verify your email
              </p>

              <p>
                Click on the verification button in the email we sent to <span className='fw-400 color_text-gray--01'>{info.email}</span>, this helps keep your account secure.
              </p>
              <p>No email in your inbox or spam folder? Let's resend it.</p>

              <Button variant='primary' type='button' className='form_btn w-100' name='step3' onClick={handleResend}>
                Resend Email
              </Button>
            </div>
          ) : null}
        </Form>
      </div>
    </div>
  )
}

export default withOutAuth(Signup)
